/**
 * Numbers, money, dates and quantities, as an email writes them.
 *
 * Handover §5.12 *Number and date formats*: forint amounts grouped in threes
 * from four digits with a non-breaking space (*3 900 Ft*), counts by AkH. 12
 * (*1200*), decimal comma (*8,8*), a true minus sign, and *Ft* never wrapped
 * away from its number. Dates `2026. 10. 05.` with the weekday where §11.6
 * asks for it, and the Összesítés event line with the month written out
 * (§5.11 point 1).
 *
 * The same rules live in `apps/web/src/page/format.ts`, which is the page's
 * and is bilingual. This one is Hungarian only, because §12 `default_lang`
 * says emails are `hu` at launch and `orders.lang` is constrained to it.
 * `test/format.test.ts` pins the two against each other's worked examples so
 * a change to one is visible as a failure in the other's figures.
 */

const NBSP = ' '
const MINUS = '−'

/** Groups from four digits when `groupFromFour`, which money always does. */
export function n(value: number, decimals = 0, groupFromFour = false): string {
  const negative = value < 0
  const [whole, fraction] = Math.abs(Number(value) || 0).toFixed(decimals).split('.')
  const digits = whole ?? ''
  const grouped =
    digits.length <= 4 && !groupFromFour ? digits : digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP)
  return (negative ? MINUS : '') + grouped + (fraction ? ',' + fraction : '')
}

/** *164 710 Ft*, with the space before *Ft* non-breaking (§5.12). */
export const ft = (value: number): string => n(Math.round(Number(value) || 0), 0, true) + NBSP + 'Ft'

const WEEKDAYS = ['vasárnap', 'hétfő', 'kedd', 'szerda', 'csütörtök', 'péntek', 'szombat'] as const

const MONTHS = [
  'január', 'február', 'március', 'április', 'május', 'június',
  'július', 'augusztus', 'szeptember', 'október', 'november', 'december',
] as const

/**
 * A date as it arrives from the database: `YYYY-MM-DD`, or a timestamp whose
 * date part is read in Europe/Budapest by the database before it gets here.
 * Parsed by hand rather than through `new Date(...)`, because `Date` would
 * read the string as UTC and could move the day across a zone boundary.
 */
export function parseDate(value: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ''))
  if (!match) return null
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) }
}

/** The day of the week of a date, by Zeller, so no `Date` and no zone. */
export function weekday(value: string): string {
  const parsed = parseDate(value)
  if (parsed === null) return ''
  const { y, m, d } = parsed
  const mm = m < 3 ? m + 12 : m
  const yy = m < 3 ? y - 1 : y
  const k = yy % 100
  const j = Math.floor(yy / 100)
  const h = (d + Math.floor((13 * (mm + 1)) / 5) + k + Math.floor(k / 4) + Math.floor(j / 4) + 5 * j) % 7
  // Zeller's h: 0 = Saturday. The array starts at Sunday.
  return WEEKDAYS[(h + 6) % 7] ?? ''
}

/** `2026. 10. 05.`, and with the weekday `2026. 10. 05. hétfő` (§11.6). */
export function date(value: string, withWeekday = false): string {
  const parsed = parseDate(value)
  if (parsed === null) return ''
  const base = `${parsed.y}. ${String(parsed.m).padStart(2, '0')}. ${String(parsed.d).padStart(2, '0')}.`
  return withWeekday ? `${base} ${weekday(value)}` : base
}

/** §5.11 point 1: the Összesítés event line writes the month out —
 *  *2026. október 16., péntek*. */
export function longDate(value: string): string {
  const parsed = parseDate(value)
  if (parsed === null) return ''
  return `${parsed.y}. ${MONTHS[parsed.m - 1]} ${parsed.d}., ${weekday(value)}`
}

/** `12:00:00` and `12:00` both arrive; the email shows `12:00`. */
export function time(value: string | null | undefined): string {
  const match = /^(\d{2}):(\d{2})/.exec(String(value ?? ''))
  return match ? `${match[1]}:${match[2]}` : ''
}

/** A timestamptz as the database hands it over, shown in Europe/Budapest.
 *  The database does the conversion (`at time zone 'Europe/Budapest'`); this
 *  only reads the date and the clock out of the result. */
/** A date and a time, as the page prints a deadline: *2026. 10. 15. csütörtök
 *  09:05*. Run 12 item 3c (a bug its check found): the weekday was missing,
 *  so U22's payment deadline and U7, U15, U17 and U19 read differently from
 *  the page and from U2. */
export function dateTime(value: string | null | undefined): string {
  const raw = String(value ?? '')
  const d = date(raw, true)
  const t = time(raw.slice(11))
  if (d === '') return ''
  return t === '' ? d : `${d} ${t}`
}

/** §5.11 *Quantities*: *db* for pieces, *adag* for salads, sticks and fruit.
 *  The unit travels on the line; a line without one shows the count alone
 *  rather than a guessed unit. */
export const qty = (quantity: number, unit?: string | null): string =>
  unit ? `${n(quantity)}${NBSP}${unit}` : n(quantity)

/**
 * B73 (31 October 2026): a day named without a relative word, for a subject
 * line — *hétfőn, november 2-án*. U12 used to say *holnap*, which is wrong
 * whenever the next business day is not the next calendar day (a Friday
 * reminder for a Monday collection).
 */
const WEEKDAY_ON: Record<string, string> = {
  'vasárnap': 'vasárnap', 'hétfő': 'hétfőn', 'kedd': 'kedden', 'szerda': 'szerdán',
  'csütörtök': 'csütörtökön', 'péntek': 'pénteken', 'szombat': 'szombaton',
}
// The suffix follows the spoken last word: 1-jén, 2-án, 4-én, 10-én, 20-án, 30-án.
const DAY_SUFFIX_UNITS = ['', 'jén', 'án', 'án', 'én', 'én', 'án', 'én', 'án', 'én'] as const
const DAY_SUFFIX_TENS: Record<number, string> = { 10: 'én', 20: 'án', 30: 'án' }

export function daySuffixed(d: number): string {
  return `${d}-${d % 10 === 0 ? DAY_SUFFIX_TENS[d] ?? 'én' : DAY_SUFFIX_UNITS[d % 10]}`
}

export function onDay(value: string): string {
  const parsed = parseDate(value)
  if (parsed === null) return ''
  return `${WEEKDAY_ON[weekday(value)] ?? weekday(value)}, ${MONTHS[parsed.m - 1]} ${daySuffixed(parsed.d)}`
}

/**
 * The same date with the closing dot removed, for a suffix: *2026. 10. 05-ig*.
 *
 * `date()` ends in the dot §5.12 writes, and Hungarian drops that dot before
 * a suffix. Without this the refund sentence read *2026. 10. 05.-ig*.
 */
export const dateSuffixable = (value: string | null | undefined): string =>
  date(String(value ?? '')).replace(/\.$/, '')

/**
 * A percentage, *30%*, closed up.
 *
 * **Barna's ruling 7 of 5 October 2026** (`DECISIONS_LOG.md`;
 * `build/FIXES.md` item 43): *30%*, *27%*, *ÁFA 27%*, *30%-a* — no space
 * between a number and the sign, on the page, in every email, in every print
 * view and in rev8's own prose. That settles the open point `028_emails`
 * left and replaces the rule this function used to carry, which put a
 * non-breaking space before the bare sign because rev7's own prose writes
 * *30 %*. The ruling wins over rev7; rev8 absorbs it.
 *
 * What the old rule was trying to prevent — a sign that wraps away from its
 * number — the closed-up form prevents outright: there is nothing to wrap at.
 *
 * It is still one function, so two strings in one email cannot spell the same
 * figure two ways, which is what `hu-copy` found between `feeBlock` and
 * `FEE_LADDER`. A percentage that carries a suffix was always closed up in
 * Hungarian — *30%-a*, *27%-os*, §14's *100%-ban növényi alapú* — and those
 * stay literals in the copy rather than being built here; the ruling means
 * the bare form is now spelled the same way.
 *
 * `tests/unit/percent-sign.test.mjs` scans the whole build for a digit, a
 * space and a sign, so the form cannot come back by hand somewhere else.
 */
export const pct = (value: number, decimals = 0): string => `${n(value, decimals)}%`

/**
 * Specification §3.7 (DECIDED 20261023): the straight-line distance staff see
 * beside the zone, to 0,1 km — *11,9 km*. Empty where there is none: a pickup,
 * or an order written before distances were stored.
 */
export const km = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return ''
  const number = Number(value)
  return Number.isFinite(number) ? `${n(number, 1)}${NBSP}km` : ''
}

/**
 * The VAT row's label, *ÁFA 27%*, closed up.
 *
 * Handover rev7 writes this string twice and disagrees with itself: §5.11
 * point 4 has *ÁFA 27 %* with a space, §5.4 *ÁFA 27%* without one. That is
 * DR-16, and Barna settled it on 4 October (`build/FIXES.md` item 19,
 * option A): **the page keeps the closed-up form and rev8 corrects §5.11.**
 *
 * `osszesites.ts` reproduces the page's Összesítés in the customer emails
 * (§11.5, §19.5 step 1), and it was following §5.11 as written — so the same
 * customer read *ÁFA 27%* on the page and *ÁFA 27 %* in the confirmation of
 * the order they had just placed. One function, used by the customer block
 * and by the internal money block, so the two cannot drift apart again.
 *
 * Ruling 7 of 5 October has since made this the rule for **every** bare
 * percentage rather than for this label alone, and `pct()` now writes the
 * same form. The two therefore agree, and this function stays because
 * *ÁFA* belongs to the label and not to the figure.
 */
export const vatLabel = (vatPct: number): string => `ÁFA ${n(vatPct)}%`

/** Hungarian chooses *a* or *az* by the sound of the next word.
 *  `apps/web/src/page/format.ts` has the page's copy of this rule (DR-20). */
export function article(value: number, capitalised = false): string {
  const v = Math.abs(Math.round(Number(value) || 0))
  const chosen = v === 1 || v === 5 || (v >= 50 && v <= 59) ? 'az' : 'a'
  return capitalised ? chosen.charAt(0).toUpperCase() + chosen.slice(1) : chosen
}

export { NBSP, MINUS }
